import React, { useEffect, useState, useCallback, useRef } from 'react';
import { adminApi } from '@/lib/api';
import { openConfirm, openPrompt } from '@/components/admin/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Coins, Save, Wallet, Plus, Search, UserPlus, X, Loader2, Mail, Image, Crown, KeyRound, GraduationCap, Library, Eye, EyeOff, Download, FileArchive, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { PanelHeader } from './_shared';
import BookstoreAdminSection from './BookstoreAdminSection';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
// iter55u — Two sub-components moved to sibling files to trim this
// panel's line count and complexity.
import TierManagerButton, { TIERS, TIER_STYLE } from './UsersPanelTierManager';
import ArchiveUserButton from './UsersPanelArchiveButton';
import { AdminUserEmailDialog } from './AdminUserEmailDialog';
import { AdminSetPasswordDialog } from './AdminSetPasswordDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';

const ROLES = ['admin', 'editor', 'contributor', 'ordinary'];
const PAGE_SIZE = 100;

function NewUserBar({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'ordinary', user_type: 'regular' });
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const create = async () => {
    if (!form.email || !form.name || form.password.length < 8) {
      return toast.error('Email, name, and an 8+ character password are required.');
    }
    if (form.password !== passwordConfirm) return toast.error('The password entries do not match.');
    setBusy(true);
    try {
      const r = await adminApi.createUser({ ...form, password_confirm: passwordConfirm });
      toast.success(`Created ${r.user.email}; password credential verified.`);
      setOpen(false);
      setForm({ email: '', name: '', password: '', role: 'ordinary', user_type: 'regular' });
      setPasswordConfirm(''); setPasswordVisible(false);
      onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Create failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4 rounded-md border border-border bg-card p-3" data-testid="admin-users-new-bar">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setOpen((v) => !v)}
                data-testid="admin-add-user-toggle"
                className="inline-flex h-8 items-center gap-1.5 rounded-sm bg-primary px-3 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90">
          <UserPlus className="h-3.5 w-3.5" /> {open ? 'Cancel' : 'Add User'}
        </button>
        <span className="text-[11.5px] text-muted-foreground">
          For team-based collaboration, manage memberships under{' '}
          <span className="font-semibold">Teams &amp; Projects</span>.
        </span>
      </div>
      {open && (
        <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-6" data-testid="admin-add-user-form">
          <Input value={form.email} onChange={set('email')} placeholder="email@domain.com" type="email" className="h-8 text-[12px]" data-testid="admin-add-user-email" />
          <Input value={form.name} onChange={set('name')} placeholder="Display name" className="h-8 text-[12px]" data-testid="admin-add-user-name" />
          <div className="relative">
            <Input value={form.password} onChange={set('password')} placeholder="Password (8+)" type={passwordVisible ? 'text' : 'password'} minLength={8} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} className="h-8 pr-9 text-[12px]" data-testid="admin-add-user-password" />
            <button type="button" onClick={() => setPasswordVisible((value) => !value)} className="absolute inset-y-0 right-0 grid w-9 place-items-center text-muted-foreground hover:text-foreground" aria-label={passwordVisible ? 'Hide password entries' : 'Show password entries'} title={passwordVisible ? 'Hide passwords' : 'Show passwords'} data-testid="admin-add-user-password-visibility">{passwordVisible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}</button>
          </div>
          <Input value={passwordConfirm} onChange={(event) => setPasswordConfirm(event.target.value)} placeholder="Confirm password" type={passwordVisible ? 'text' : 'password'} minLength={8} autoComplete="new-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} className="h-8 text-[12px]" data-testid="admin-add-user-password-confirm" />
          <select value={form.role} onChange={set('role')} className="h-8 rounded-md border border-border bg-background px-2 text-[12px]" data-testid="admin-add-user-role">
            <option value="ordinary">ordinary</option>
            <option value="admin">admin</option>
          </select>
          <div className="flex gap-1.5">
            <select value={form.user_type} onChange={set('user_type')} className="h-8 flex-1 rounded-md border border-border bg-background px-2 text-[12px]" data-testid="admin-add-user-type">
              <option value="regular">regular</option>
              <option value="special">project-funded</option>
            </select>
            <Button size="sm" onClick={create} disabled={busy} className="h-8 gap-1.5" data-testid="admin-add-user-submit">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Create
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}



export default function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [pool, setPool] = useState(0);
  const [poolAcquire, setPoolAcquire] = useState('');
  const [busy, setBusy] = useState(true);
  const [creditOpen, setCreditOpen] = useState(null);
  const [credit, setCredit] = useState({ tokens: '', note: '' });
  // iter-16 — Copyscape grant (separate wallet).
  const [csOpen, setCsOpen] = useState(null);
  const [csForm, setCsForm] = useState({ tokens: '', note: '' });
  // iter-51s — Canva free-cover grant.
  const [canvaOpen, setCanvaOpen] = useState(null);
  const [canvaCount, setCanvaCount] = useState(1);
  // iter55n — Bulk tier grant.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTier, setBulkTier] = useState('promo');
  const [bulkExpiry, setBulkExpiry] = useState('');
  const [bulkNote, setBulkNote] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [trainingBusy, setTrainingBusy] = useState(null);
  const [libraryBusy, setLibraryBusy] = useState(null);
  // Manuscript export state
  const [exportPreviewUser,    setExportPreviewUser]    = useState(null); // user obj
  const [exportPreviewData,    setExportPreviewData]    = useState(null); // preview response
  const [exportPreviewLoading, setExportPreviewLoading] = useState(false);
  const [exportDownloading,    setExportDownloading]    = useState(null); // user_id string
  const [passwordTarget, setPasswordTarget] = useState(null);

  // ── Search & filter ──────────────────────────────────────────────
  // `searchQuery` is the live input value; `committedQ` is debounced
  // and used as the actual `?q=` server parameter.
  const [searchQuery, setSearchQuery] = useState('');
  const [committedQ,  setCommittedQ]  = useState('');
  const [roleFilter,  setRoleFilter]  = useState('all');
  const [tierFilter,  setTierFilter]  = useState('all');
  const debounceRef = useRef(null);

  // ── Pagination ───────────────────────────────────────────────────
  const [skip,  setSkip]  = useState(0);
  const [total, setTotal] = useState(0);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    setSelected(new Set());
    setSkip(0);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setCommittedQ(val), 300);
  };
  const clearSearch = () => {
    setSearchQuery('');
    setCommittedQ('');
    setSelected(new Set());
    setSkip(0);
  };
  const clearAllFilters = () => {
    setSearchQuery('');
    setCommittedQ('');
    setRoleFilter('all');
    setTierFilter('all');
    setSelected(new Set());
    setSkip(0);
  };


  const toggleTrainingAccreditation = async (user) => {
    setTrainingBusy(user.user_id);
    try {
      const next = !user.training_accredited;
      await adminApi.setTrainingAccreditation(user.user_id, next);
      toast.success(`${next ? 'Granted' : 'Revoked'} Training accreditation for ${user.email}.`);
      await reload();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not update Training accreditation.');
    } finally { setTrainingBusy(null); }
  };

  const toggleLoomLibraryAccess = async (user) => {
    setLibraryBusy(user.user_id);
    try {
      const next = !user.loom_library_access;
      await adminApi.setLoomLibraryAccess(user.user_id, next);
      toast.success(`${next ? 'Granted' : 'Revoked'} Manuscript Library access for ${user.email}.`);
      await reload();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not update Manuscript Library access.');
    } finally { setLibraryBusy(null); }
  };

  const submitCanva = async (uid) => {
    const count = parseInt(canvaCount, 10);
    if (!count || count < 1) return toast.error('Enter a positive number of free covers');
    try {
      const r = await adminApi.canvaGrantFree(uid, count);
      toast.success(`Granted ${count} free Canva cover${count === 1 ? '' : 's'}. Total: ${r.free_canva_covers}.`);
      setCanvaOpen(null); setCanvaCount(1);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Grant failed'); }
  };

  const submitCopyscape = async (uid) => {
    const tokens = parseInt(csForm.tokens, 10);
    if (!tokens || Number.isNaN(tokens)) return toast.error('Enter a Copyscape token amount');
    try {
      const r = await adminApi.copyscapeGrant(uid, tokens, csForm.note);
      toast.success(`Applied ${r.applied_delta} Copyscape tokens. Balance: ${r.copyscape_tokens}.`);
      setCsOpen(null); setCsForm({ tokens: '', note: '' });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Grant failed'); }
  };

  const toggleUserType = async (uid, currentType) => {
    const next = currentType === 'special' ? 'regular' : 'special';
    try {
      await adminApi.setUserType(uid, next);
      toast.success(`User flipped to ${next}.`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not flip user_type'); }
  };

  const sendResetLink = async (uid, email) => {
    if (!(await openConfirm({
      title: `Password reset for ${email}?`,
      description: 'A reset email will be sent, and any prior reset links for this user become invalid immediately.',
      confirmLabel: 'Send reset email',
    }))) return;
    try {
      const r = await adminApi.sendResetLink(uid);
      if (r.delivered) {
        toast.success(`Reset email sent to ${email}.`);
      } else {
        const reason = r.delivery_error || 'The provider did not accept the message.';
        toast.error(`Email not delivered: ${reason}`, { duration: 12000 });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send reset link.'); }
  };

  const saveUserPassword = async (newPassword, reason) => {
    await adminApi.setUserPassword(passwordTarget.user_id, newPassword, reason);
    toast.success(`Password set for ${passwordTarget.email}. Share it via a secure channel.`);
  };

  // `reload` has no dependencies on props/state, so we wrap it in
  // `useCallback` with an empty dep list to give it a stable identity.
  // That lets us add it to the mount-effect's dep array without causing
  // the effect to re-fire — and silences the
  // `react-hooks/exhaustive-deps` warning that the prior code
  // suppressed with an empty literal `[]`.
  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const params = { skip, limit: PAGE_SIZE };
      if (committedQ)         params.q    = committedQ;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (tierFilter !== 'all') params.tier = tierFilter; // backend handles 'free' as {$in:[null,""]}
      const [u, p] = await Promise.all([adminApi.users(params), adminApi.pool()]);
      setUsers(u.users || []);
      setTotal(u.total || 0);
      setPool(p.admin_token_pool || 0);
    } catch { toast.error('Could not load users'); }
    finally { setBusy(false); }
  }, [skip, committedQ, roleFilter, tierFilter]);
  useEffect(() => { reload(); }, [reload]);

  const eligibleUsers = users;
  // user (including admins) is eligible now that the backend allows
  // tier grants regardless of role. Operators frequently need to put
  // themselves on Pro/Premier to test tier-gated flows.
  const exportCSV = useCallback(async () => {
    // Use the backend CSV export endpoint which exports ALL filtered users (not just current page)
    try {
      toast.loading('Preparing CSV export…', { id: 'csv-export' });
      const params = {};
      if (committedQ)                params.q    = committedQ;
      if (roleFilter && roleFilter !== 'all') params.role = roleFilter;
      if (tierFilter && tierFilter !== 'all') params.tier = tierFilter;
      const blob = await adminApi.exportCsvBlob(params);
      const url  = URL.createObjectURL(blob);
      const a    = Object.assign(document.createElement('a'), {
        href: url, download: `users-export-${new Date().toISOString().slice(0,10)}.csv`,
      });
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success('CSV exported successfully', { id: 'csv-export' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'CSV export failed', { id: 'csv-export' });
    }
  }, [committedQ, roleFilter, tierFilter]);

  // ── Manuscript bulk export ────────────────────────────────────────────────
  const openManuscriptPreview = useCallback(async (user) => {
    setExportPreviewUser(user);
    setExportPreviewData(null);
    setExportPreviewLoading(true);
    try {
      const data = await adminApi.manuscriptExportPreview(user.user_id);
      setExportPreviewData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load project list.');
      setExportPreviewUser(null);
    } finally {
      setExportPreviewLoading(false);
    }
  }, []);

  const downloadManuscripts = useCallback(async (userId, displayName) => {
    setExportDownloading(userId);
    const tid = toast.loading(`Building manuscript archive for ${displayName}…`);
    try {
      const res  = await adminApi.manuscriptExportDownload(userId);
      const blob = res.data;
      const url  = URL.createObjectURL(blob);
      const a    = Object.assign(document.createElement('a'), {
        href: url,
        download: `${displayName.replace(/\s+/g,'_')}_manuscripts_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.zip`,
      });
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded manuscripts for ${displayName}`, { id: tid });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Download failed.', { id: tid });
    } finally {
      setExportDownloading(null);
    }
  }, []);
  const toggleOne = (uid) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(uid)) next.delete(uid); else next.add(uid);
    return next;
  });
  const allSelected  = eligibleUsers.length > 0 && selected.size === eligibleUsers.length;
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => {
    if (allSelected || someSelected) setSelected(new Set());
    else setSelected(new Set(eligibleUsers.map((u) => u.user_id)));
  };
  const submitBulkGrant = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const body = { tier: bulkTier || null, note: bulkNote || null };
      if (bulkTier && bulkTier !== 'free' && bulkExpiry) {
        body.expires_at = new Date(`${bulkExpiry}T23:59:59Z`).toISOString();
      }
      const r = await adminApi.grantManySubscriptions([...selected], body);
      const tierLabel = bulkTier || 'cleared';
      const bits = [`Applied ${tierLabel} to ${r.applied}/${r.requested} user(s)`];
      if (r.skipped) bits.push(`${r.skipped} skipped (missing users)`);
      toast.success(bits.join(' · '));
      setSelected(new Set());
      setBulkOpen(false);
      setBulkNote('');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk grant failed.');
    } finally {
      setBulkBusy(false);
    }
  };

  const submitCredit = async (uid) => {
    const tokens = parseInt(credit.tokens, 10);
    if (!tokens || Number.isNaN(tokens)) return toast.error('Enter a token amount');
    try {
      const r = await adminApi.credit(uid, tokens, credit.note);
      toast.success(`Applied ${r.applied_delta.toLocaleString()} tokens. Pool: ${r.admin_token_pool.toLocaleString()}.`);
      setCreditOpen(null); setCredit({ tokens: '', note: '' });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Credit failed'); }
  };

  const setRole = async (uid, role) => {
    try {
      await adminApi.setRole(uid, role);
      toast.success(`Role updated → ${role}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Role change failed'); }
  };

  const acquirePool = async () => {
    const tokens = parseInt(poolAcquire, 10);
    if (!tokens || tokens <= 0) return toast.error('Enter a positive number');
    try {
      const r = await adminApi.acquirePool(tokens, 'admin acquire');
      toast.success(`Pool ↑ to ${r.admin_token_pool.toLocaleString()}.`);
      setPoolAcquire('');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Acquire failed'); }
  };

  return (
    <section>
      <PanelHeader title="Users & Tokens" subtitle="Manage customer accounts, change global roles, and credit tokens from the admin pool." onRefresh={reload} />

      {/* iter-26 — quick CRUD for new users + nudge towards Teams panel */}
      <NewUserBar onCreated={reload} />

      {/* Admin token pool widget */}
      <div className="mt-4 flex flex-wrap items-end gap-3 rounded-md border border-border bg-card p-4" data-testid="admin-token-pool">
        <div className="flex items-center gap-2 pr-4">
          <Wallet className="h-5 w-5 text-primary" />
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Admin token pool</div>
            <div className="font-mono-stat text-[18px] font-semibold" data-testid="admin-pool-balance">{pool.toLocaleString()}</div>
          </div>
        </div>
        <div>
          <Label className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Acquire</Label>
          <div className="mt-1 flex gap-2">
            <Input type="number" min="1" placeholder="e.g. 1000000" value={poolAcquire} onChange={(e) => setPoolAcquire(e.target.value)} className="h-8 w-44 font-mono-stat text-[12px]" data-testid="admin-pool-acquire-input" />
            <Button size="sm" onClick={acquirePool} className="h-8 gap-1.5" data-testid="admin-pool-acquire-btn"><Plus className="h-3.5 w-3.5" /> Add to pool</Button>
          </div>
        </div>
        <div className="ml-auto text-[10.5px] text-muted-foreground max-w-xs">
          Credits to users come out of this pool. Negative credits return tokens to the pool.
        </div>
      </div>

      <div className="mt-4 rounded-md border border-border bg-card">
        {/* iter55n — Bulk action bar (only when ≥1 row selected). */}
        {selected.size > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-primary/5 px-3 py-2 text-[12px]"
               data-testid="admin-users-bulk-bar">
            <div className="font-medium text-primary">
              {selected.size} user{selected.size === 1 ? '' : 's'} selected
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-[11.5px]"
                      onClick={() => setSelected(new Set())}
                      data-testid="admin-users-bulk-clear">Clear</Button>
              <Button size="sm" onClick={() => setBulkOpen(true)}
                      disabled={bulkBusy}
                      className="h-7 gap-1 text-[11.5px]"
                      data-testid="admin-users-bulk-grant-open">
                <Crown className="h-3 w-3" /> Grant tier
              </Button>
              {/* iter55f85 — Batch archive/delete. */}
              <Button size="sm" variant="outline" className="h-7 gap-1 text-[11.5px]"
                      onClick={async () => {
                        if (!(await openConfirm({
                          title: `Archive ${selected.size} user${selected.size === 1 ? '' : 's'}?`,
                          description: 'Their projects move into the archive collection — restorable any time from Admin → Archived Users.',
                          confirmLabel: 'Archive',
                          tone: 'destructive',
                        }))) return;
                        setBulkBusy(true);
                        try {
                          const r = await adminApi.batchArchiveUsers([...selected], 'admin batch archive');
                          toast.success(`Archived ${r.archived}/${r.requested}${r.skipped ? ` · ${r.skipped} skipped (admins/missing)` : ''}.`);
                          setSelected(new Set());
                          reload();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Batch archive failed.');
                        } finally { setBulkBusy(false); }
                      }}
                      disabled={bulkBusy}
                      data-testid="admin-users-bulk-archive">
                Archive
              </Button>
              <Button size="sm" variant="destructive" className="h-7 gap-1 text-[11.5px]"
                      onClick={async () => {
                        const answer = await openPrompt({
                          title: `Hard-delete ${selected.size} user${selected.size === 1 ? '' : 's'}?`,
                          description:
                            'IRREVERSIBLE. Their user doc, projects, chapters, sessions, and analytics events are erased. ' +
                            'Payment/royalty ledgers are preserved. Type OK to confirm.',
                          placeholder: 'Type OK',
                          confirmLabel: 'Hard-delete',
                        });
                        if (answer !== 'OK') return;
                        setBulkBusy(true);
                        try {
                          const r = await adminApi.batchDeleteUsers([...selected], 'admin batch delete');
                          toast.success(`Deleted ${r.deleted}/${r.requested} · ${r.projects_deleted} projects erased${r.skipped ? ` · ${r.skipped} skipped (admins)` : ''}.`);
                          setSelected(new Set());
                          reload();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Batch delete failed.');
                        } finally { setBulkBusy(false); }
                      }}
                      disabled={bulkBusy}
                      data-testid="admin-users-bulk-delete">
                Delete forever
              </Button>
            </div>
          </div>
        )}

        {/* ── Search & filter bar ──────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-panel-header/10 px-3 py-2">
          <div className="relative min-w-[180px] flex-1 max-w-sm">
            <svg className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <Input
              type="text"
              placeholder="Search by name, email or user ID…"
              value={searchQuery}
              onChange={handleSearchChange}
              className="h-8 pl-8 pr-7 text-[12.5px]"
              data-testid="admin-users-search"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <svg className="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12"/></svg>
              </button>
            )}
          </div>
          <select
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setSelected(new Set()); setSkip(0); }}
            className="h-8 rounded-sm border border-border bg-background px-2 text-[12px] text-foreground"
            data-testid="admin-users-role-filter"
          >
            <option value="all">All roles</option>
            <option value="ordinary">Ordinary</option>
            <option value="editor">Editor</option>
            <option value="contributor">Contributor</option>
            <option value="admin">Admin</option>
          </select>
          <select
            value={tierFilter}
            onChange={(e) => { setTierFilter(e.target.value); setSelected(new Set()); setSkip(0); }}            className="h-8 rounded-sm border border-border bg-background px-2 text-[12px] text-foreground"
            data-testid="admin-users-tier-filter"
          >
            <option value="all">All tiers</option>
            <option value="free">Free</option>
            <option value="author">Author</option>
            <option value="publisher">Publisher</option>
            <option value="studio">Studio</option>
            <option value="promo">Promo</option>
          </select>
          <span className="ml-auto shrink-0 text-[11px] text-muted-foreground" data-testid="admin-users-count">
            {(committedQ || roleFilter !== 'all' || tierFilter !== 'all')
              ? <>{users.length} <span className="opacity-60">of {total} matched</span></>
              : <>{total} users</>}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCSV}
            className="h-8 shrink-0 gap-1.5 text-[12px]"
            data-testid="admin-users-export-csv"
            title={`Export ${users.length} user${users.length !== 1 ? 's' : ''} (current page) to CSV`}
          >
            <svg className="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            CSV
          </Button>
        </div>

        <div className="grid grid-cols-12 gap-2 border-b border-border bg-panel-header/40 px-3 py-2 text-[10.5px] uppercase tracking-wider text-muted-foreground">
          <div className="col-span-3 flex items-center gap-2">
            <Checkbox
              checked={allSelected ? true : someSelected ? 'indeterminate' : false}
              onCheckedChange={toggleAll}
              disabled={eligibleUsers.length === 0}
              data-testid="admin-users-select-all"
              aria-label="Select all non-admin users"
            />
            <span>User</span>
          </div>
          <div className="col-span-2">Email</div>
          <div className="col-span-1">Role</div>
          <div className="col-span-1 text-right">Tokens</div>
          <div className="col-span-1 text-right" title="Copyscape token wallet (separate from AI tokens)">CS</div>
          <div className="col-span-4 text-right">Actions</div>
        </div>
        {busy && <div className="px-3 py-6 text-[12px] text-muted-foreground">Loading…</div>}
        {!busy && total === 0 && !committedQ && roleFilter === 'all' && tierFilter === 'all' && (
          <div className="px-3 py-6 text-[12px] text-muted-foreground">No users yet.</div>
        )}
        {!busy && users.length === 0 && (committedQ || roleFilter !== 'all' || tierFilter !== 'all') && (
          <div className="px-3 py-8 text-center text-[12px] text-muted-foreground">
            No users match your search or filters.{' '}
            <button type="button" className="underline hover:text-foreground" onClick={clearAllFilters}>
              Clear filters
            </button>
          </div>
        )}
        {users.map((u) => {
          const isSel = selected.has(u.user_id);
          return (
          <div key={u.user_id}>
            <div className={`grid grid-cols-12 items-center gap-2 border-b border-border px-3 py-2 text-[12.5px] ${
              isSel ? 'bg-primary/5' : ''
            }`}
                 data-testid={`admin-user-row-${u.user_id}`}
                 data-selected={isSel ? '1' : '0'}>
              <div className="col-span-3 flex items-center gap-2 min-w-0">
                <Checkbox
                  checked={isSel}
                  onCheckedChange={() => toggleOne(u.user_id)}
                  data-testid={`admin-user-select-${u.user_id}`}
                  aria-label={`Select ${u.email || u.user_id}`}
                />
                {u.picture
                  ? <img src={u.picture} alt="" className="h-6 w-6 rounded-full border border-border" />
                  : <div className="h-6 w-6 rounded-full bg-secondary" />
                }
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="truncate font-medium">{u.name || u.email || u.user_id}</span>
                    <TierManagerButton user={u} onUpdated={reload} />
                  </div>
                  <div className="flex items-center gap-1.5 font-mono-stat text-[10.5px] text-muted-foreground truncate"><span>{u.user_id}</span><span className={`rounded-sm border px-1 py-px font-sans text-[9px] ${u.has_password ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-700' : 'border-amber-400/40 bg-amber-500/10 text-amber-800'}`} data-testid={`admin-user-password-ready-${u.user_id}`}>{u.has_password ? 'Password ready' : 'No password'}</span></div>
                </div>
              </div>
              <div className="col-span-2 truncate text-[12px] text-muted-foreground min-w-0" title={u.email}>{u.email}</div>
              <div className="col-span-1 min-w-0">
                <select
                  value={u.role || 'ordinary'}
                  onChange={(e) => setRole(u.user_id, e.target.value)}
                  className="h-7 w-full rounded-sm border border-border bg-background px-1.5 text-[11.5px] capitalize"
                  data-testid={`admin-user-role-${u.user_id}`}
                >
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div className="col-span-1 text-right font-mono-stat tabular-nums whitespace-nowrap min-w-0" data-testid={`admin-user-tokens-${u.user_id}`}>{(u.tokens_purchased || 0).toLocaleString()}</div>
              <div
                className="col-span-1 text-right font-mono-stat tabular-nums whitespace-nowrap text-primary min-w-0"
                data-testid={`admin-user-copyscape-${u.user_id}`}
                title="Copyscape token wallet"
              >{(u.copyscape_tokens || 0).toLocaleString()}</div>
              <div className="col-span-4 text-right flex flex-wrap justify-end items-center gap-1 min-w-0">
                {u.is_system_ai ? (
                  <span className="inline-flex h-7 items-center gap-1 rounded-sm border border-amber-400/40 bg-amber-500/10 px-2 text-[10.5px] font-semibold text-amber-800" data-testid={`admin-library-system-protected-${u.user_id}`}><Library className="h-3.5 w-3.5" /> Protected source</span>
                ) : (
                  <Button size="sm" variant="ghost" className={`h-7 gap-1 text-[11.5px] ${u.loom_library_access ? 'text-emerald-700' : 'text-muted-foreground'}`} onClick={() => toggleLoomLibraryAccess(u)} disabled={libraryBusy === u.user_id} data-testid={`admin-loom-library-access-${u.user_id}`} title={u.loom_library_access ? 'Revoke Manuscript Library access' : 'Grant Manuscript Library access'}>
                    {libraryBusy === u.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Library className="h-3.5 w-3.5" />}
                    {u.loom_library_access ? 'Library granted' : 'Manuscript Library'}
                  </Button>
                )}
                <Button
                  size="sm" variant="ghost"
                  className={`h-7 gap-1 text-[11.5px] ${u.training_accredited ? 'text-emerald-700' : 'text-muted-foreground'}`}
                  onClick={() => toggleTrainingAccreditation(u)}
                  disabled={trainingBusy === u.user_id}
                  data-testid={`admin-training-accreditation-${u.user_id}`}
                  title={u.training_accredited ? 'Revoke Training accreditation' : 'Grant Training accreditation'}
                >
                  {trainingBusy === u.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GraduationCap className="h-3.5 w-3.5" />}
                  {u.training_accredited ? 'Accredited' : 'Training'}
                </Button>
                <button
                  onClick={() => toggleUserType(u.user_id, u.user_type)}
                  data-testid={`admin-user-type-${u.user_id}`}
                  className={`rounded-sm px-1.5 py-0.5 text-[10.5px] font-mono-stat hover:opacity-90 ${
                    u.user_type === 'special'
                      ? 'bg-primary/15 text-primary border border-primary/30'
                      : 'bg-muted text-muted-foreground border border-border'
                  }`}
                  title={u.user_type === 'special' ? 'Project-funded mode uses a project wallet first. Click to return to the user wallet.' : 'Click to route paid actions through project-scoped wallets.'}
                >
                  {u.user_type === 'special' ? '★ project-funded' : 'regular wallet'}
                </button>
                <Button size="sm" variant="ghost" className="h-7 gap-1 text-[11.5px]" onClick={() => { setCreditOpen(creditOpen === u.user_id ? null : u.user_id); setCsOpen(null); setCanvaOpen(null); }} data-testid={`admin-credit-toggle-${u.user_id}`}>
                  <Coins className="h-3.5 w-3.5" /> AI
                </Button>
                <Button size="sm" variant="ghost" className="h-7 gap-1 text-[11.5px] text-primary" onClick={() => { setCsOpen(csOpen === u.user_id ? null : u.user_id); setCreditOpen(null); setCanvaOpen(null); }} data-testid={`admin-copyscape-toggle-${u.user_id}`}>
                  <Search className="h-3.5 w-3.5" /> CS
                </Button>
                <Button size="sm" variant="ghost" className="h-7 gap-1 text-[11.5px] text-fuchsia-700 hover:text-fuchsia-900" onClick={() => { setCanvaOpen(canvaOpen === u.user_id ? null : u.user_id); setCreditOpen(null); setCsOpen(null); }} data-testid={`admin-canva-toggle-${u.user_id}`}>
                  <Image className="h-3.5 w-3.5" /> Canva
                </Button>
                <Button
                  size="sm" variant="ghost"
                  className="h-7 gap-1 text-[11.5px] text-muted-foreground hover:text-foreground"
                  onClick={() => sendResetLink(u.user_id, u.email)}
                  data-testid={`admin-send-reset-${u.user_id}`}
                  title="Send password-reset email (email-login users only)"
                >
                  <Mail className="h-3.5 w-3.5" /> Reset
                </Button>
                {/* Feb-2026 — Manual password override for support ops. */}
                <Button
                  size="sm" variant="ghost" className="h-7 gap-1.5 text-[11.5px]"
                  onClick={() => setPasswordTarget(u)}
                  data-testid={`admin-set-password-${u.user_id}`}
                  title="Set a new password manually (bcrypt-hashed, audit-logged)"
                >
                  <KeyRound className="h-3.5 w-3.5" /> Set password
                </Button>
                <AdminUserEmailDialog user={u} onUpdated={reload} />
                <Button
                  size="sm" variant="ghost"
                  className="h-7 gap-1 text-[11.5px] text-sky-700 hover:text-sky-900"
                  onClick={() => openManuscriptPreview(u)}
                  disabled={exportDownloading === u.user_id}
                  data-testid={`admin-download-manuscripts-${u.user_id}`}
                  title="Download all manuscripts as ZIP"
                >
                  {exportDownloading === u.user_id
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Download className="h-3.5 w-3.5" />}
                  Manuscripts
                </Button>
                <ArchiveUserButton user={u} onArchived={reload} />
              </div>
            </div>
            {creditOpen === u.user_id && (
              <div className="border-b border-border bg-panel-header/30 px-3 py-3">
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <Label className="text-[11px]">Tokens (positive = from pool → user; negative = user → pool)</Label>
                    <Input
                      data-testid={`admin-credit-tokens-input-${u.user_id}`}
                      value={credit.tokens}
                      onChange={(e) => setCredit({ ...credit, tokens: e.target.value })}
                      className="h-8 w-44 font-mono-stat text-[12px]"
                      placeholder="100000"
                      type="number"
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-[11px]">Note (optional)</Label>
                    <Input value={credit.note} onChange={(e) => setCredit({ ...credit, note: e.target.value })} className="h-8 text-[12px]" placeholder="Promo / refund / support" />
                  </div>
                  <Button size="sm" className="h-8 gap-1.5" onClick={() => submitCredit(u.user_id)} data-testid={`admin-credit-submit-${u.user_id}`}>
                    <Save className="h-3.5 w-3.5" /> Apply
                  </Button>
                </div>
                <div className="mt-1 text-[10.5px] text-muted-foreground">Current pool: <span className="font-mono-stat">{pool.toLocaleString()}</span></div>
              </div>
            )}
            {csOpen === u.user_id && (
              <div className="border-b border-border bg-primary/5 px-3 py-3" data-testid={`admin-copyscape-form-${u.user_id}`}>
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <Label className="text-[11px] flex items-center gap-1.5">
                      <Search className="h-3 w-3 text-primary" />
                      Copyscape tokens (1 token = 1 scan by default)
                    </Label>
                    <Input
                      data-testid={`admin-copyscape-tokens-input-${u.user_id}`}
                      value={csForm.tokens}
                      onChange={(e) => setCsForm({ ...csForm, tokens: e.target.value })}
                      className="h-8 w-44 font-mono-stat text-[12px]"
                      placeholder="50"
                      type="number"
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <Label className="text-[11px]">Note (optional)</Label>
                    <Input value={csForm.note} onChange={(e) => setCsForm({ ...csForm, note: e.target.value })} className="h-8 text-[12px]" placeholder="Bundle / one-off sale" />
                  </div>
                  <Button size="sm" className="h-8 gap-1.5" onClick={() => submitCopyscape(u.user_id)} data-testid={`admin-copyscape-submit-${u.user_id}`}>
                    <Save className="h-3.5 w-3.5" /> Grant
                  </Button>
                </div>
                <div className="mt-1 text-[10.5px] text-muted-foreground">Negative numbers claw back. Each scan debits the per-scan cost set under API Keys → Plagiarism Provider.</div>
              </div>
            )}
            {canvaOpen === u.user_id && (
              <div className="border-b border-border bg-fuchsia-50/50 px-3 py-3" data-testid={`admin-canva-form-${u.user_id}`}>
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <Label className="text-[11px] flex items-center gap-1.5">
                      <Image className="h-3 w-3 text-fuchsia-700" />
                      Free Canva covers (each bypasses the cash wallet)
                    </Label>
                    <Input
                      data-testid={`admin-canva-count-input-${u.user_id}`}
                      value={canvaCount}
                      onChange={(e) => setCanvaCount(e.target.value)}
                      className="h-8 w-32 font-mono-stat text-[12px]"
                      placeholder="1"
                      type="number"
                      min={1}
                      max={100}
                    />
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Current free covers:{' '}
                    <span className="font-mono-stat font-semibold text-fuchsia-900">
                      {u.free_canva_covers || 0}
                    </span>
                  </div>
                  <Button size="sm" className="h-8 gap-1.5 bg-fuchsia-700 hover:bg-fuchsia-800" onClick={() => submitCanva(u.user_id)} data-testid={`admin-canva-submit-${u.user_id}`}>
                    <Save className="h-3.5 w-3.5" /> Grant
                  </Button>
                </div>
                <div className="mt-1 text-[10.5px] text-muted-foreground">Each free cover skips the user&apos;s cash wallet on the next billable Canva op (design create / export). Useful for promo grants or one-off goodwill.</div>
              </div>
            )}
          </div>
          );
        })}
      </div>

      {/* Pagination controls */}
      {total > PAGE_SIZE && (
        <div className="mt-2 flex items-center justify-between px-1 text-[12px] text-muted-foreground" data-testid="admin-users-pagination">
          <span>
            Showing {skip + 1}–{Math.min(skip + users.length, total)} of {total} users
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-[11.5px]"
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              disabled={skip === 0 || busy}
              data-testid="admin-users-prev-page"
            >
              Previous
            </Button>
            <span className="text-[11px] tabular-nums">
              Page {Math.floor(skip / PAGE_SIZE) + 1} of {Math.ceil(total / PAGE_SIZE)}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-[11.5px]"
              onClick={() => setSkip(skip + PAGE_SIZE)}
              disabled={skip + PAGE_SIZE >= total || busy}
              data-testid="admin-users-next-page"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <BookstoreAdminSection />
      <AdminSetPasswordDialog
        user={passwordTarget}
        open={Boolean(passwordTarget)}
        onOpenChange={(open) => !open && setPasswordTarget(null)}
        onSave={saveUserPassword}
      />

      {/* iter55n — Bulk tier-grant confirmation dialog. */}
      <AlertDialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <AlertDialogContent data-testid="admin-users-bulk-grant-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="inline-flex items-center gap-2">
              <Crown className="h-4 w-4 text-primary" />
              Grant tier to {selected.size} user{selected.size === 1 ? '' : 's'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Applies the same tier + expiry to every selected user. Admin-role
              users are always skipped. Great for onboarding a launch-day cohort
              or comping a giveaway winner list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px]">Tier</Label>
              <div className="mt-1 grid grid-cols-2 gap-1.5">
                {TIERS.map((t) => (
                  <button key={t.id || 'none'}
                          onClick={() => { setBulkTier(t.id); if (t.id === 'free') setBulkExpiry(''); }}
                          data-testid={`admin-users-bulk-tier-${t.id || 'none'}`}
                          data-active={bulkTier === t.id ? '1' : '0'}
                          className={`rounded-md border p-2 text-left transition-colors ${
                            bulkTier === t.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/60'
                          }`}>
                    <div className="text-[11.5px] font-semibold">{t.label}</div>
                    <div className="text-[10px] text-muted-foreground">{t.hint}</div>
                  </button>
                ))}
              </div>
            </div>
            {bulkTier && bulkTier !== 'free' && (
              <div>
                <Label className="text-[11px]">Expires (leave blank for +1 year)</Label>
                <Input type="date" value={bulkExpiry}
                       onChange={(e) => setBulkExpiry(e.target.value)}
                       className="h-8 text-[12px] mt-1"
                       data-testid="admin-users-bulk-expiry" />
              </div>
            )}
            {bulkTier === 'free' && (
              <div className="rounded-sm border border-teal-300 bg-teal-50 p-2 text-[10.5px] text-teal-900" data-testid="admin-users-bulk-free-internal">
                Internal test cohort · no expiration · hidden from public pricing and signup.
              </div>
            )}
            <div>
              <Label className="text-[11px]">Note (audit log)</Label>
              <Input value={bulkNote}
                     onChange={(e) => setBulkNote(e.target.value)}
                     placeholder="e.g. Launch-day cohort · signed up before Mar 1"
                     className="h-8 text-[12px] mt-1"
                     data-testid="admin-users-bulk-note" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); submitBulkGrant(); }}
              disabled={bulkBusy}
              data-testid="admin-users-bulk-grant-submit">
              {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Apply to {selected.size} user{selected.size === 1 ? '' : 's'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Manuscript Export Preview Dialog ─────────────────────────────── */}
      {exportPreviewUser && (
        <Dialog
          open={!!exportPreviewUser}
          onOpenChange={(v) => { if (!v) { setExportPreviewUser(null); setExportPreviewData(null); } }}
        >
          <DialogContent className="max-w-lg gap-0 p-0" data-testid="admin-manuscript-export-dialog">
            <DialogHeader className="border-b border-border px-5 py-4">
              <DialogTitle className="flex items-center gap-2 text-base">
                <FileArchive className="h-4 w-4 text-sky-600" />
                Manuscripts — {exportPreviewUser.name || exportPreviewUser.email}
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[55vh] overflow-y-auto px-5 py-4 space-y-3">
              {exportPreviewLoading ? (
                <div className="flex items-center gap-2 text-[12px] text-muted-foreground py-4">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading projects…
                </div>
              ) : exportPreviewData ? (
                <>
                  <p className="text-[11.5px] text-muted-foreground">
                    {exportPreviewData.project_count} project{exportPreviewData.project_count !== 1 ? 's' : ''} will be exported as DOCX files with full metadata appendix.
                  </p>
                  {exportPreviewData.projects?.length === 0 ? (
                    <p className="text-[12px] text-muted-foreground italic">No projects found for this user.</p>
                  ) : (
                    <div className="rounded-md border border-border bg-muted/30 text-[12px]">
                      {exportPreviewData.projects?.map((p) => (
                        <div key={p.project_id} className="flex items-center justify-between gap-2 border-b border-border/50 px-3 py-1.5 last:border-b-0">
                          <span className="truncate font-medium">{p.resolved_title || p.raw_title || 'Untitled'}</span>
                          <span className="shrink-0 text-muted-foreground">{p.chapter_count ?? 0} ch</span>
                          {p.is_hash_title && (
                            <Badge variant="outline" className="shrink-0 text-[9px] border-amber-400/50 text-amber-700 bg-amber-500/10">resolved</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : null}
            </div>
            <div className="border-t border-border px-5 py-3 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => { setExportPreviewUser(null); setExportPreviewData(null); }}>
                Cancel
              </Button>
              <Button
                size="sm"
                className="gap-1.5"
                disabled={exportDownloading === exportPreviewUser?.user_id || exportPreviewLoading || (exportPreviewData && exportPreviewData.project_count === 0)}
                onClick={() => {
                  const u = exportPreviewUser;
                  downloadManuscripts(u.user_id, u.name || u.email || u.user_id);
                  setExportPreviewUser(null);
                  setExportPreviewData(null);
                }}
                data-testid="admin-manuscript-export-download-btn"
              >
                {exportDownloading === exportPreviewUser?.user_id
                  ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Building…</>
                  : <><Download className="h-3.5 w-3.5" />Download ZIP</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}
